import { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";
import type { Payment } from "../../../store/api/paymentsApi";
import { useUpdatePaymentMutation } from "../../../store/api/paymentsApi";

interface RecordPaymentModalProps {
	isOpen: boolean;
	onClose: () => void;
	selectedPayments: Payment[];
	onSuccess: () => void;
}

const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
	{ value: "bank_transfer", label: "Bank Transfer" },
	{ value: "cash", label: "Cash" },
	{ value: "check", label: "Check" },
	{ value: "online", label: "Online" },
	{ value: "mobile_money", label: "Mobile Money" },
	{ value: "other", label: "Other" },
];

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
	isOpen,
	onClose,
	selectedPayments,
	onSuccess,
}) => {
	const [updatePayment, { isLoading: isSubmitting }] =
		useUpdatePaymentMutation();
	const [formData, setFormData] = useState({
		paid_date: new Date().toISOString().split("T")[0],
		payment_method: "bank_transfer",
		reference_number: "",
		notes: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (selectedPayments?.length === 1 && isOpen) {
			setFormData((prev) => ({
				...prev,
				reference_number: selectedPayments[0].reference_number || "",
			}));
		} else if (isOpen) {
			setFormData({
				paid_date: new Date().toISOString().split("T")[0],
				payment_method: "bank_transfer",
				reference_number: "",
				notes: "",
			});
		}
	}, [selectedPayments, isOpen]);

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.paid_date) {
			newErrors.paid_date = "Payment date is required";
		}

		if (!formData.payment_method) {
			newErrors.payment_method = "Payment method is required";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) return;

		setErrors({});

		try {
			// Update each selected payment
			await Promise.all(
				selectedPayments.map((payment) =>
					updatePayment({
						id: payment.id,
						data: {
							paid_date: formData.paid_date,
							payment_method: formData.payment_method as any,
							status: "paid",
							reference_number: formData.reference_number || undefined,
							notes: formData.notes || undefined,
						},
					}).unwrap(),
				),
			);

			// Reset form and close modal
			setFormData({
				paid_date: new Date().toISOString().split("T")[0],
				payment_method: "bank_transfer",
				reference_number: "",
				notes: "",
			});
			setErrors({});
			onSuccess();
		} catch (error: any) {
			console.error("Error recording payment:", error);
			if (error?.data?.error) {
				setErrors({ submit: error.data.error });
			} else {
				setErrors({
					submit: "Failed to record payment. Please try again.",
				});
			}
		}
	};

	const handleCancel = () => {
		setFormData({
			paid_date: new Date().toISOString().split("T")[0],
			payment_method: "bank_transfer",
			reference_number: "",
			notes: "",
		});
		setErrors({});
		onClose();
	};

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
		}).format(amount);
	};

	const totalAmount =
		selectedPayments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black/50 z-1300 flex items-center justify-center p-4">
			<div className="bg-card border border-border rounded-lg modal-shadow w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
				{/* Header */}
				<div className="p-6 border-b border-border">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Record Payment
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Recording payment for {selectedPayments?.length || 0} payment
								{selectedPayments?.length !== 1 ? "s" : ""}
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={handleCancel}
							className="text-muted-foreground hover:text-foreground"
						>
							<Icon name="X" size={20} />
						</Button>
					</div>
				</div>

				{/* Content */}
				<div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
					{/* Selected Payments Summary */}
					{selectedPayments?.length > 0 && (
						<div className="bg-muted/30 rounded-lg p-4 mb-6">
							<h3 className="font-medium text-foreground mb-3">
								Payment Details
							</h3>
							<div className="space-y-2">
								{selectedPayments.map((payment) => (
									<div
										key={payment.id}
										className="flex items-center justify-between text-sm"
									>
										<div>
											<span className="text-foreground">
												{payment.tenant_name || "N/A"}
											</span>
											<span className="text-muted-foreground ml-2">
												({payment.property_name || "N/A"})
											</span>
										</div>
										<span className="font-medium text-foreground">
											{formatCurrency(payment.amount)}
										</span>
									</div>
								))}

								{selectedPayments.length > 1 && (
									<div className="flex items-center justify-between text-sm pt-2 border-t border-border">
										<span className="font-medium text-foreground">
											Total Amount
										</span>
										<span className="font-bold text-foreground">
											{formatCurrency(totalAmount)}
										</span>
									</div>
								)}
							</div>
						</div>
					)}

					{errors.submit && (
						<div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
							{errors.submit}
						</div>
					)}

					{/* Payment Form */}
					<form onSubmit={handleSubmit} className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Payment Date */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Payment Date <span className="text-destructive">*</span>
								</label>
								<Input
									type="date"
									value={formData.paid_date}
									onChange={(e) =>
										handleInputChange("paid_date", e.target.value)
									}
									error={errors.paid_date}
									max={new Date().toISOString().split("T")[0]}
									required
								/>
							</div>

							{/* Payment Method */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Payment Method <span className="text-destructive">*</span>
								</label>
								<Select
									value={formData.payment_method}
									options={PAYMENT_METHOD_OPTIONS}
									onValueChange={(value) =>
										handleInputChange("payment_method", value as string)
									}
									required
								/>
								{errors.payment_method && (
									<p className="text-sm text-destructive mt-1">
										{errors.payment_method}
									</p>
								)}
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Payment Reference */}
							<div>
								<Input
									label="Reference Number"
									type="text"
									placeholder="Transaction ID, Check #, etc."
									value={formData.reference_number}
									onChange={(e) =>
										handleInputChange("reference_number", e.target.value)
									}
								/>
							</div>
						</div>

						{/* Notes */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Notes
							</label>
							<textarea
								rows={3}
								placeholder="Additional notes about this payment..."
								value={formData.notes}
								onChange={(e) => handleInputChange("notes", e.target.value)}
								className="w-full px-3 py-2 border border-border rounded-lg text-foreground bg-background placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
							/>
						</div>
					</form>
				</div>

				{/* Footer */}
				<div className="p-6 border-t border-border bg-muted/20">
					<div className="flex flex-col sm:flex-row items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
						<Button
							variant="outline"
							onClick={handleCancel}
							disabled={isSubmitting}
							className="w-full sm:w-auto"
						>
							Cancel
						</Button>

						<Button
							onClick={handleSubmit}
							disabled={isSubmitting}
							className="w-full sm:w-auto"
						>
							{isSubmitting ? (
								<>
									<Icon
										name="Loader2"
										size={16}
										className="mr-2 animate-spin"
									/>
									Recording...
								</>
							) : (
								<>
									<Icon name="CheckCircle" size={16} className="mr-2" />
									Record Payment
								</>
							)}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RecordPaymentModal;
